import { PiFileCsvBold } from "react-icons/pi";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Mail, Phone, X } from "lucide-react";
import {
    liveStreamTicketsCollection,
    ordersCollection,
    eventsCollection,
} from "../utils/firebase";
import { formatFirestoreTimestamp } from "../utils/common";
import { ADMIN_TABLE_FETCH_SIZE } from "../constants";
import useDebounce from "../hooks/use-debounce";
import {
    getDocs,
    limit,
    orderBy,
    query,
    startAfter,
    where,
    doc,
    getDoc,
    setDoc,
    serverTimestamp,
} from "firebase/firestore";
import toast from "react-hot-toast";
import ManageLiveStreamTicket from "./manage-live-stream-ticket";

const ManageLiveStreamTickets = () => {
    const [tickets, setTickets] = useState([]);
    const [lastDoc, setLastDoc] = useState(null);
    const [sort, setSort] = useState("createdAt");
    const [order, setOrder] = useState("desc");
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [ticketsWithOrderDetails, setTicketsWithOrderDetails] = useState([]);
    const [eventsCache, setEventsCache] = useState({});
    const [ordersCache, setOrdersCache] = useState({});
    const [filterEventId, setFilterEventId] = useState("");

    // Create Ticket modal state
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [eventsList, setEventsList] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState("");
    // Export modal state
    const [isExportOpen, setIsExportOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const size = ADMIN_TABLE_FETCH_SIZE;
    const debouncedSearchTerm = useDebounce(searchTerm, 1000);
    const observerRef = useRef(null);
    const loadingRef = useRef(false);

    const handleRowClick = (ticket) => {
        setSelectedTicket(ticket);
    };

    const handleRemoveSelectedTicket = () => {
        setSelectedTicket(null);
    };

    const openCreateModal = async () => {
        setIsCreateOpen(true);
        try {
            // Fetch latest events for selection (limit to 100 for performance)
            const q = query(
                eventsCollection,
                orderBy("createdAt", "desc"),
                limit(100)
            );
            const snapshot = await getDocs(q);
            const list = snapshot.docs.map((d) => ({
                id: d.id,
                name: d.data()?.name || d.id,
            }));
            setEventsList(list);
        } catch (e) {
            console.error("Failed to fetch events for creation:", e);
            setEventsList([]);
        }
    };

    const closeCreateModal = () => {
        if (isCreating) return;
        setIsCreateOpen(false);
        setSelectedEventId("");
    };

    const openExportModal = () => {
        if (!filterEventId) {
            return toast.error("Please select an event to export");
        }
        setIsExportOpen(true);
    };

    const closeExportModal = () => {
        if (isExporting) return;
        setIsExportOpen(false);
    };

    const formatTimestampForCsv = (ts) => {
        if (!ts) return "";
        const d = ts?.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
        const pad = (n) => String(n).padStart(2, "0");
        const yyyy = d.getFullYear();
        const mm = pad(d.getMonth() + 1);
        const dd = pad(d.getDate());
        const HH = pad(d.getHours());
        const MM = pad(d.getMinutes());
        return `${yyyy}-${mm}-${dd} ${HH}:${MM}`;
    };

    const csvEscape = (value) => {
        if (value === null || value === undefined) return "";
        const str = String(value);
        if (/[",\n]/.test(str)) {
            return '"' + str.replace(/"/g, '""') + '"';
        }
        return str;
    };

    const handleExportConfirm = async () => {
        if (!filterEventId) {
            return toast.error("Please select an event to export");
        }
        setIsExporting(true);
        try {
            // Fetch all tickets for selected event in batches
            const batchSize = 500;
            let allTickets = [];
            let last = null;
            while (true) {
                const constraints = [
                    where("eventId", "==", filterEventId),
                    orderBy("createdAt", "desc"),
                    limit(batchSize),
                ];
                if (last) constraints.push(startAfter(last));
                const q = query(liveStreamTicketsCollection, ...constraints);
                const snap = await getDocs(q);
                const batch = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                allTickets = allTickets.concat(batch);
                if (snap.docs.length < batchSize) break;
                last = snap.docs[snap.docs.length - 1];
            }

            // Fetch related orders
            const orderIds = Array.from(
                new Set(allTickets.map((t) => t.orderId).filter((v) => !!v))
            );
            const ordersMap = {};
            await Promise.all(
                orderIds.map(async (oid) => {
                    try {
                        const oDoc = await getDoc(doc(ordersCollection, oid));
                        if (oDoc.exists()) ordersMap[oid] = oDoc.data();
                    } catch (e) {
                        console.error("Failed fetching order:", oid, e);
                    }
                })
            );

            // Resolve event name
            let eventName =
                eventsList.find((e) => e.id === filterEventId)?.name || "";
            if (!eventName) {
                try {
                    const eDoc = await getDoc(
                        doc(eventsCollection, filterEventId)
                    );
                    if (eDoc.exists()) eventName = eDoc.data()?.name || "";
                } catch (e) {
                    console.error("Failed fetching event name:", e);
                }
            }
            if (!eventName) eventName = filterEventId;

            // Build CSV
            const headers = [
                "Ticket Code",
                "Event",
                "Customer Name",
                "Email",
                "Phone",
                "Status",
                "Created At",
            ];
            const rows = allTickets.map((t) => {
                const order = t.orderId ? ordersMap[t.orderId] : undefined;
                const status = t.isActivated ? "Active" : "Inactive";
                return [
                    csvEscape(t.code || ""),
                    csvEscape(eventName),
                    csvEscape(order?.name || ""),
                    csvEscape(order?.email || ""),
                    csvEscape(order?.phoneNumber || ""),
                    csvEscape(status),
                    csvEscape(formatTimestampForCsv(t.createdAt)),
                ].join(",");
            });
            const csv = [headers.join(","), ...rows].join("\n");

            // Filename: (event name - Live Stream Ticket - yyyy mm dd, HH MM)
            const now = new Date();
            const pad = (n) => String(n).padStart(2, "0");
            const yyyy = now.getFullYear();
            const mm = pad(now.getMonth() + 1);
            const dd = pad(now.getDate());
            const HH = pad(now.getHours());
            const MM = pad(now.getMinutes());
            const safeEventName = eventName.replace(/[\\/:*?"<>|]/g, "-");
            const filename = `${safeEventName} - Live Stream Ticket - ${yyyy} ${mm} ${dd}, ${HH} ${MM}.csv`;

            const blob = new Blob(["\ufeff" + csv], {
                type: "text/csv;charset=utf-8;",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            toast.success(`Exported ${allTickets.length} tickets`);
            setIsExportOpen(false);
        } catch (e) {
            console.error("Export failed:", e);
            toast.error("Failed to export CSV");
        } finally {
            setIsExporting(false);
        }
    };

    const generateRandomCode = () => {
        return Math.floor(100000 + Math.random() * 900000).toString();
    };

    const handleCreateTicket = async () => {
        if (!selectedEventId) {
            return toast.error("Please select an event");
        }
        setIsCreating(true);
        try {
            const MAX_ATTEMPTS = 20;
            let attempt = 0;
            let created = false;
            let code = null;

            while (attempt < MAX_ATTEMPTS && !created) {
                attempt++;
                code = generateRandomCode();
                const newDocRef = doc(
                    liveStreamTicketsCollection,
                    `${selectedEventId}_${code}`
                );
                const existing = await getDoc(newDocRef);
                if (existing.exists()) {
                    code = null;
                    continue;
                }
                await setDoc(newDocRef, {
                    orderId: null,
                    eventId: selectedEventId,
                    code: code,
                    createdAt: serverTimestamp(),
                    updateAt: serverTimestamp(),
                    isActivated: false,
                });
                created = true;
            }

            if (!created) {
                throw new Error("Unable to generate unique code");
            }

            toast.success("Live stream ticket created");
            // Refresh list from the start to include the new ticket
            setTickets([]);
            setLastDoc(null);
            setHasMore(true);
            await fetchData(true);
            closeCreateModal();
        } catch (e) {
            console.error("Create ticket failed:", e);
            toast.error("Failed to create ticket");
        } finally {
            setIsCreating(false);
        }
    };

    const fetchMissingData = async (tickets) => {
        // Collect unique event IDs and order IDs that we need to fetch
        const eventIdsToFetch = new Set();
        const orderIdsToFetch = new Set();

        tickets.forEach((ticket) => {
            if (ticket.eventId && !eventsCache[ticket.eventId]) {
                eventIdsToFetch.add(ticket.eventId);
            }
            if (ticket.orderId && !ordersCache[ticket.orderId]) {
                orderIdsToFetch.add(ticket.orderId);
            }
        });

        // Fetch missing events
        const newEvents = {};
        if (eventIdsToFetch.size > 0) {
            const eventPromises = Array.from(eventIdsToFetch).map(
                async (eventId) => {
                    try {
                        const eventDoc = await getDoc(
                            doc(eventsCollection, eventId)
                        );
                        if (eventDoc.exists()) {
                            return { id: eventId, data: eventDoc.data() };
                        }
                    } catch (error) {
                        console.error(
                            `Error fetching event ${eventId}:`,
                            error
                        );
                    }
                    return null;
                }
            );

            const eventResults = await Promise.all(eventPromises);
            eventResults.forEach((result) => {
                if (result) {
                    newEvents[result.id] = result.data;
                }
            });
        }

        // Fetch missing orders
        const newOrders = {};
        if (orderIdsToFetch.size > 0) {
            const orderPromises = Array.from(orderIdsToFetch).map(
                async (orderId) => {
                    try {
                        const orderDoc = await getDoc(
                            doc(ordersCollection, orderId)
                        );
                        if (orderDoc.exists()) {
                            return { id: orderId, data: orderDoc.data() };
                        }
                    } catch (error) {
                        console.error(
                            `Error fetching order ${orderId}:`,
                            error
                        );
                    }
                    return null;
                }
            );

            const orderResults = await Promise.all(orderPromises);
            orderResults.forEach((result) => {
                if (result) {
                    newOrders[result.id] = result.data;
                }
            });
        }

        // Update caches
        if (Object.keys(newEvents).length > 0) {
            setEventsCache((prev) => ({ ...prev, ...newEvents }));
        }
        if (Object.keys(newOrders).length > 0) {
            setOrdersCache((prev) => ({ ...prev, ...newOrders }));
        }

        // Combine ticket data with cached data
        const ticketsWithDetails = tickets.map((ticket) => {
            const orderData =
                ordersCache[ticket.orderId] || newOrders[ticket.orderId];
            const eventData =
                eventsCache[ticket.eventId] || newEvents[ticket.eventId];

            return {
                ...ticket,
                name: orderData?.name || "N/A",
                email: orderData?.email || "N/A",
                phoneNumber: orderData?.phoneNumber || "N/A",
                eventName: eventData?.name || "N/A",
            };
        });

        // Apply email search filter
        const filteredTickets =
            debouncedSearchTerm.trim() !== ""
                ? ticketsWithDetails.filter((ticket) =>
                      ticket.email
                          .toLowerCase()
                          .includes(debouncedSearchTerm.toLowerCase())
                  )
                : ticketsWithDetails;

        setTicketsWithOrderDetails(filteredTickets);
    };

    const handleTableHeaderClick = (field) => {
        if (field === sort) {
            setOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
        } else {
            setSort(field);
            setOrder("asc");
        }
    };

    const buildQuery = useCallback(
        (reset = false) => {
            const constraints = [];

            // Note: We can't search by email directly in Firestore since email is in orders collection
            // We'll fetch all tickets and filter by email in the frontend
            if (filterEventId) {
                constraints.push(where("eventId", "==", filterEventId));
            }
            constraints.push(orderBy(sort, order));
            constraints.push(limit(size));

            if (!reset && !!lastDoc) {
                constraints.push(startAfter(lastDoc));
            }

            return query(liveStreamTicketsCollection, ...constraints);
        },
        [lastDoc, order, size, sort, filterEventId]
    );

    const fetchData = useCallback(
        async (reset = false) => {
            if (loadingRef.current) return;
            loadingRef.current = true;
            setLoading(true);

            try {
                const q = buildQuery(reset);
                const snapshot = await getDocs(q);
                const fetchedTickets =
                    snapshot?.docs?.map((doc) => {
                        const data = doc.data();
                        return {
                            id: doc.id,
                            ...data,
                        };
                    }) ?? [];

                setTickets((prev) => {
                    if (reset) {
                        return fetchedTickets;
                    } else {
                        const arr = [...prev, ...fetchedTickets];
                        const seenIds = new Set();
                        const uniqueData = arr.filter((item) => {
                            if (seenIds.has(item.id)) {
                                return false;
                            }
                            seenIds.add(item.id);
                            return true;
                        });
                        return uniqueData;
                    }
                });

                // Fetch missing data for the tickets
                const allTickets = reset
                    ? fetchedTickets
                    : [...tickets, ...fetchedTickets];
                fetchMissingData(allTickets);

                const lastVisible =
                    snapshot.docs[snapshot.docs.length - 1] || null;
                setLastDoc(lastVisible);

                setHasMore(snapshot.docs.length === size);
            } catch (error) {
                console.error("Error fetching tickets data: ", error);
            }
            loadingRef.current = false;
            setLoading(false);
        },
        [buildQuery, size]
    );

    useEffect(() => {
        setTickets([]);
        setLastDoc(null);
        setHasMore(true);
        fetchData(true);
    }, [sort, order, filterEventId]);

    // Load events list for filter dropdown on mount
    useEffect(() => {
        (async () => {
            try {
                const q = query(
                    eventsCollection,
                    orderBy("createdAt", "desc"),
                    limit(100)
                );
                const snapshot = await getDocs(q);
                const list = snapshot.docs.map((d) => ({
                    id: d.id,
                    name: d.data()?.name || d.id,
                }));
                setEventsList(list);
            } catch (e) {
                console.error("Failed to fetch events for filter:", e);
            }
        })();
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            const firstEntry = entries[0];
            if (firstEntry.isIntersecting && hasMore && !loadingRef.current) {
                fetchData();
            }
        });

        const currentObserverRef = observerRef.current;
        if (currentObserverRef) {
            observer.observe(currentObserverRef);
        }
        return () => {
            if (currentObserverRef) {
                observer.unobserve(currentObserverRef);
            }
        };
    }, [hasMore, fetchData]);

    // Handle search filtering
    useEffect(() => {
        if (tickets.length > 0) {
            fetchMissingData(tickets);
        }
    }, [debouncedSearchTerm]);

    const headers = {
        code: "Ticket Code",
        eventName: "Event",
        name: "Customer Name",
        email: "Email",
        phoneNumber: "Phone",
        isActivated: "Status",
        createdAt: "Created At",
    };

    return (
        <div className="flex flex-row w-full h-full">
            <div className="flex-center flex-1 items-start w-full overflow-auto h-full">
                <div className="overflow-auto w-full h-full">
                    <table className="table w-auto">
                        {/* head */}
                        <thead>
                            <tr className="[&>th]:text-center">
                                <th></th>
                                {Object.keys(headers).map((key, index) => {
                                    const header = headers[key];
                                    return (
                                        <th
                                            key={key}
                                            className="hover:bg-base-200 cursor-pointer"
                                            onClick={() =>
                                                handleTableHeaderClick(key)
                                            }
                                        >
                                            <div className="flex flex-row justify-center items-center gap-2">
                                                <p>{header}</p>
                                                {sort === key ? (
                                                    order === "desc" ? (
                                                        <ChevronUp />
                                                    ) : (
                                                        <ChevronDown />
                                                    )
                                                ) : null}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {ticketsWithOrderDetails.map((ticket, index) => {
                                return (
                                    <tr
                                        key={index}
                                        className={
                                            "hover [&>td]:text-nowrap cursor-pointer " +
                                            (ticket.id === selectedTicket?.id
                                                ? "!bg-base-300"
                                                : "")
                                        }
                                        onClick={() => handleRowClick(ticket)}
                                    >
                                        <th>{index + 1}</th>
                                        <td className="font-mono text-sm">
                                            {ticket.code}
                                        </td>
                                        <td className="truncate max-w-48">
                                            {ticket.eventName}
                                        </td>
                                        <td>{ticket.name}</td>
                                        <td>
                                            <div className="border flex flex-row items-center justify-between gap-1 cursor-pointer">
                                                <p
                                                    className="pl-2 truncate w-48"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(
                                                            ticket.email
                                                        );
                                                        toast(
                                                            "📋 Copied Email to Clipboard"
                                                        );
                                                    }}
                                                >
                                                    {ticket.email}
                                                </p>
                                                <a
                                                    className="btn btn-ghost btn-sm h-full btn-square"
                                                    href={`mailto:${ticket.email}`}
                                                >
                                                    <Mail className="h-4" />
                                                </a>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="border flex flex-row items-center justify-between gap-1 cursor-pointer">
                                                <p
                                                    className="pl-2 truncate w-48"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(
                                                            ticket.phoneNumber
                                                        );
                                                        toast(
                                                            "📋 Copied Phone No to Clipboard"
                                                        );
                                                    }}
                                                >
                                                    {ticket.phoneNumber}
                                                </p>
                                                <a
                                                    className="btn btn-ghost btn-sm h-full btn-square"
                                                    target="_blank"
                                                    href={`tel:${ticket.phoneNumber}`}
                                                >
                                                    <Phone className="h-4" />
                                                </a>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex-center">
                                                <span
                                                    className={`badge ${
                                                        ticket.isActivated
                                                            ? "badge-success"
                                                            : "badge-warning"
                                                    }`}
                                                >
                                                    {ticket.isActivated
                                                        ? "Active"
                                                        : "Inactive"}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="text-sm">
                                            {formatFirestoreTimestamp(
                                                ticket.createdAt
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div className="flex-center w-full p-2">
                        {/* Display messages based on state */}
                        {loading && <div className="loading loading-bars" />}
                        {!loading && ticketsWithOrderDetails.length === 0 && (
                            <p className="flex-center h-36">NO RESULT</p>
                        )}
                        {!loading &&
                            ticketsWithOrderDetails.length > 0 &&
                            !hasMore && <p className="opacity-70">END</p>}
                    </div>
                    {/* Sentinel element for the Intersection Observer */}
                    <div ref={observerRef} style={{ height: "1px" }} />
                </div>
                <div className="divider divider-primary m-0" />
                {/* Bottom Search Bar */}
                <div className="w-full">
                    <div className="flex-center flex-row p-4 pt-2 gap-2">
                        <select
                            className="select select-bordered w-full max-w-xs"
                            value={filterEventId}
                            onChange={(e) => setFilterEventId(e.target.value)}
                        >
                            <option value="">All Events</option>
                            {eventsList.map((ev) => (
                                <option key={ev.id} value={ev.id}>
                                    {ev.name}
                                </option>
                            ))}
                        </select>
                        <button
                            className="btn btn-primary whitespace-nowrap btn-square"
                            onClick={openExportModal}
                            disabled={!filterEventId}
                        >
                            <PiFileCsvBold size={18} />
                        </button>
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            placeholder="Search by Email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button
                            className="btn btn-primary whitespace-nowrap"
                            onClick={openCreateModal}
                        >
                            Create Ticket
                        </button>
                    </div>
                </div>
            </div>

            {!!selectedTicket ? (
                <>
                    <div className="absolute bg-base-100 md:relative w-full md:w-1/2 md:max-w-[36rem] overflow-y-auto">
                        <ManageLiveStreamTicket
                            key={selectedTicket.id}
                            ticket={selectedTicket}
                            handleClose={handleRemoveSelectedTicket}
                        />
                    </div>
                </>
            ) : null}

            {/* Export CSV Modal */}
            {isExportOpen && (
                <div className="modal modal-open" onClick={closeExportModal}>
                    <div
                        className="modal-box relative border"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="btn btn-square btn-ghost absolute right-2 top-2"
                            onClick={closeExportModal}
                            aria-label={"Close"}
                            disabled={isExporting}
                        >
                            <X />
                        </button>
                        <h3 className="font-bold text-lg mb-4">Export CSV</h3>
                        <p className="mb-4">
                            This will export all live stream tickets for the
                            selected event.
                        </p>
                        <div className="modal-action">
                            <button
                                className="btn"
                                onClick={closeExportModal}
                                disabled={isExporting}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleExportConfirm}
                                disabled={isExporting}
                            >
                                {isExporting ? (
                                    <span className="loading loading-spinner loading-xs"></span>
                                ) : (
                                    "Confirm"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Ticket Modal */}
            {isCreateOpen && (
                <div className="modal modal-open" onClick={closeCreateModal}>
                    <div
                        className="modal-box relative border"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="btn btn-square btn-ghost absolute right-2 top-2"
                            onClick={closeCreateModal}
                            aria-label={"Close"}
                            disabled={isCreating}
                        >
                            <X />
                        </button>
                        <h3 className="font-bold text-lg mb-4">
                            Create Live Stream Ticket
                        </h3>
                        <div className="flex flex-col gap-3">
                            <label htmlFor="event" className="font-semibold">
                                Select Event
                            </label>
                            <select
                                id="event"
                                className="select select-bordered w-full"
                                value={selectedEventId}
                                onChange={(e) =>
                                    setSelectedEventId(e.target.value)
                                }
                                disabled={isCreating}
                            >
                                <option value="" disabled>
                                    Pick an event
                                </option>
                                {eventsList.map((ev) => (
                                    <option key={ev.id} value={ev.id}>
                                        {ev.name}
                                    </option>
                                ))}
                            </select>
                            <div className="text-sm opacity-70">
                                A unique 6-digit code will be generated
                                automatically.
                            </div>
                        </div>
                        <div className="modal-action">
                            <button
                                className="btn"
                                onClick={closeCreateModal}
                                disabled={isCreating}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleCreateTicket}
                                disabled={isCreating || !selectedEventId}
                            >
                                {isCreating ? (
                                    <span className="loading loading-spinner loading-xs"></span>
                                ) : (
                                    "Create"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageLiveStreamTickets;
